import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ArrowLeft, Leaf, CheckCircle, Sparkles } from "lucide-react";
import { API } from "./components/shared";
import Header from "./components/Header";
import Footer from "./components/Footer";

const questions = [
  {
    id: "goal",
    question: "What brings you here?",
    subtitle: "Select your primary health goal",
    options: [
      { value: "fertility", label: "Trying to Conceive", desc: "Planning pregnancy naturally" },
      { value: "pcos", label: "PCOS / Hormonal Issues", desc: "Irregular cycles, weight, acne" },
      { value: "ivf", label: "IVF / ART Support", desc: "Preparing for or going through IVF" },
      { value: "pregnancy", label: "Pregnancy Nutrition", desc: "Already pregnant, need guidance" },
      { value: "male", label: "Male Fertility", desc: "Sperm health & motility concerns" },
      { value: "wellness", label: "General Wellness", desc: "Family health & weight management" },
    ],
  },
  {
    id: "who",
    question: "Who is this for?",
    subtitle: "Help us personalize your plan",
    options: [
      { value: "me", label: "Just Me", desc: "Individual program" },
      { value: "partner", label: "My Partner", desc: "Support for them" },
      { value: "couple", label: "Both of Us", desc: "Couples program together" },
    ],
  },
  {
    id: "duration",
    question: "How long have you been on this journey?",
    subtitle: "This helps us understand where you are",
    options: [
      { value: "new", label: "Just Starting", desc: "Beginning to plan" },
      { value: "6m", label: "6 - 12 Months", desc: "Been trying for a while" },
      { value: "1y", label: "1 - 2 Years", desc: "Looking for answers" },
      { value: "2y+", label: "2+ Years", desc: "Need expert intervention" },
    ],
  },
  {
    id: "conditions",
    question: "Any diagnosed conditions?",
    subtitle: "Select all that apply",
    multi: true,
    options: [
      { value: "pcos", label: "PCOS" },
      { value: "thyroid", label: "Thyroid Issues" },
      { value: "endo", label: "Endometriosis" },
      { value: "low_amh", label: "Low AMH" },
      { value: "male_factor", label: "Male Factor" },
      { value: "insulin", label: "Insulin Resistance" },
      { value: "none", label: "None / Not Sure" },
    ],
  },
  {
    id: "preference",
    question: "What matters most to you?",
    subtitle: "Your top priority in a program",
    options: [
      { value: "natural", label: "Natural Approach", desc: "Diet & lifestyle first" },
      { value: "speed", label: "Quick Results", desc: "Time-sensitive goals" },
      { value: "comprehensive", label: "End-to-End Care", desc: "Full team support" },
      { value: "affordable", label: "Budget-Friendly", desc: "Best value option" },
    ],
  },
];

const getRecommendation = (answers) => {
  const { goal, who, duration, conditions, preference } = answers;

  if (goal === "pregnancy" || preference === "comprehensive") {
    return {
      program: "Fitly Premium Plus",
      slug: "premium-plus",
      tagline: "Conception to Motherhood — Complete Care",
      reason: "Based on your needs, our most comprehensive program covers your entire journey with a dedicated care team, weekly monitoring, and specialist consultations.",
      features: ["Trimester-wise nutrition plans", "Weekly progress monitoring", "Live yoga 5 days/week", "Dedicated care coordinator", "All specialist consultations included"],
      price: "85,000",
    };
  }

  if (who === "couple" || goal === "male" || (conditions && conditions.includes("male_factor"))) {
    return {
      program: "Fitly Elite — Couples Track",
      slug: "elite",
      tagline: "Together Towards Parenthood",
      reason: "Since you're working as a couple, our Couples Track addresses both partners simultaneously — cycle-synced nutrition for her and spermatogenesis-aligned support for him.",
      features: ["Dual nutrition protocols", "Couples consultations", "5-pillar fertility approach", "Gynaecologist + Andrologist access", "WhatsApp support"],
      price: "24,000",
      priceSuffix: " / 3 months",
    };
  }

  if (duration === "2y+" || goal === "ivf" || (conditions && (conditions.includes("endo") || conditions.includes("low_amh")))) {
    return {
      program: "Fitly Elite — 6 Month Track",
      slug: "elite",
      tagline: "Deep, Sustained Transformation",
      reason: "Given your journey length and conditions, a longer program gives your body the time it needs for egg quality improvement and hormonal rebalancing.",
      features: ["6-month sustained protocol", "Biomarker-informed adjustments", "Full specialist team access", "Ayurveda integration option", "Priority support"],
      price: "45,000",
      priceSuffix: " / 6 months",
    };
  }

  return {
    program: "Fitly Elite — 3 Month Track",
    slug: "elite",
    tagline: "Focused Fertility Nutrition",
    reason: "A targeted 3-month program is perfect for your goals — long enough to see real hormonal and cycle changes through precision nutrition.",
    features: ["5-pillar fertility approach", "Cycle-synced nutrition", "Gynaecologist consults", "Strength coaching", "WhatsApp daily support"],
    price: "12,000",
    priceSuffix: " / 3 months",
  };
};

const BG_IMAGE = "https://images.unsplash.com/photo-1490818387583-1baba5e638af?auto=format&fit=crop&w=1920&q=80";

export default function NutritionAssessmentPage() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const totalSteps = questions.length + 1;
  const progress = ((step + 1) / totalSteps) * 100;

  const handleSelect = (questionId, value, multi) => {
    if (multi) {
      const current = answers[questionId] || [];
      if (value === "none") {
        setAnswers({ ...answers, [questionId]: ["none"] });
      } else {
        const filtered = current.filter((v) => v !== "none");
        const updated = filtered.includes(value) ? filtered.filter((v) => v !== value) : [...filtered, value];
        setAnswers({ ...answers, [questionId]: updated });
      }
    } else {
      setAnswers({ ...answers, [questionId]: value });
      setTimeout(() => setStep((s) => Math.min(s + 1, questions.length)), 300);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const rec = getRecommendation(answers);
    try {
      await fetch(`${API}/assessment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers, contact, recommendation: rec.program }),
      });
    } catch (e) {
      // Best-effort lead capture — don't block quiz results on API failure
      if (process.env.NODE_ENV === 'development') console.warn("Assessment save failed:", e.message);
    }
    setResult(rec);
    setSubmitting(false);
  };

  const canProceed = () => {
    if (step >= questions.length) return contact.name && contact.email && contact.phone;
    const q = questions[step];
    if (q.multi) return (answers[q.id] || []).length > 0;
    return !!answers[q.id];
  };

  return (
    <div className="min-h-screen bg-[#0D1F17]" data-testid="assessment-page">
      <Helmet>
        <title>Nutrition Assessment | Fitly India</title>
        <meta name="description" content="Take our 2-minute nutrition assessment to find the right Fitly program for your fertility and wellness goals." />
      </Helmet>
      <Header />

      {/* Background */}
      <div className="fixed inset-0 z-0">
        <img src={BG_IMAGE} alt="" className="w-full h-full object-cover" aria-hidden="true" />
        <div className="absolute inset-0 bg-[#0D1F17]/[0.92]" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle, #D4AF37 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
      </div>

      <div className="relative z-10 pt-24 pb-16 min-h-screen flex flex-col">
        {!result ? (
          <div className="flex-1 flex flex-col max-w-2xl mx-auto w-full px-6">
            {/* Progress */}
            <div className="mb-8">
              <div className="flex justify-between text-[10px] tracking-[0.2em] uppercase text-[#FEFDFB]/40 mb-2">
                <span>Step {step + 1} of {totalSteps}</span>
                <span>{Math.round(progress)}% Complete</span>
              </div>
              <div className="h-1 bg-[#FEFDFB]/10 rounded-full overflow-hidden">
                <motion.div className="h-full bg-[#D4AF37] rounded-full" animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
              </div>
            </div>

            <AnimatePresence mode="wait">
              {step < questions.length ? (
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -40 }}
                  transition={{ duration: 0.3 }}
                  className="flex-1"
                >
                  <div className="mb-8">
                    <h2 className="font-serif text-3xl md:text-4xl text-[#FEFDFB] tracking-tight" data-testid={`question-${step}`}>
                      {questions[step].question}
                    </h2>
                    <p className="text-[#FEFDFB]/40 mt-2 text-sm">{questions[step].subtitle}</p>
                  </div>

                  <div className={`grid gap-3 ${questions[step].multi ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-2"}`}>
                    {questions[step].options.map((opt) => {
                      const isSelected = questions[step].multi
                        ? (answers[questions[step].id] || []).includes(opt.value)
                        : answers[questions[step].id] === opt.value;

                      return (
                        <button
                          key={opt.value}
                          onClick={() => handleSelect(questions[step].id, opt.value, questions[step].multi)}
                          data-testid={`option-${opt.value}`}
                          className={`text-left p-5 rounded-xl border transition-all duration-300 group ${
                            isSelected
                              ? "bg-[#D4AF37]/15 border-[#D4AF37]/50 shadow-lg shadow-[#D4AF37]/5"
                              : "bg-[#FEFDFB]/[0.04] border-[#FEFDFB]/10 hover:bg-[#FEFDFB]/[0.08] hover:border-[#FEFDFB]/20"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                              isSelected ? "border-[#D4AF37] bg-[#D4AF37]" : "border-[#FEFDFB]/30"
                            }`}>
                              {isSelected && <CheckCircle size={12} className="text-[#0D1F17]" />}
                            </div>
                            <div>
                              <p className={`font-medium text-sm ${isSelected ? "text-[#D4AF37]" : "text-[#FEFDFB]/80"}`}>{opt.label}</p>
                              {opt.desc && <p className="text-[11px] text-[#FEFDFB]/35 mt-0.5">{opt.desc}</p>}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {questions[step].multi && (
                    <p className="text-[11px] text-[#FEFDFB]/30 mt-3">Select all that apply, then continue</p>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="contact"
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -40 }}
                  transition={{ duration: 0.3 }}
                  className="flex-1"
                >
                  <div className="mb-8">
                    <h2 className="font-serif text-3xl md:text-4xl text-[#FEFDFB] tracking-tight" data-testid="contact-step">
                      Almost there!
                    </h2>
                    <p className="text-[#FEFDFB]/40 mt-2 text-sm">Share your details to get your personalized recommendation</p>
                  </div>
                  <div className="space-y-4 max-w-md">
                    <input
                      type="text"
                      placeholder="Your Name"
                      value={contact.name}
                      onChange={(e) => setContact({ ...contact, name: e.target.value })}
                      data-testid="assessment-name"
                      className="w-full bg-[#FEFDFB]/[0.06] border border-[#FEFDFB]/10 rounded-xl px-5 py-4 text-[#FEFDFB] placeholder-[#FEFDFB]/30 focus:border-[#D4AF37]/50 focus:outline-none transition-colors text-sm"
                    />
                    <input
                      type="email"
                      placeholder="Email Address"
                      value={contact.email}
                      onChange={(e) => setContact({ ...contact, email: e.target.value })}
                      data-testid="assessment-email"
                      className="w-full bg-[#FEFDFB]/[0.06] border border-[#FEFDFB]/10 rounded-xl px-5 py-4 text-[#FEFDFB] placeholder-[#FEFDFB]/30 focus:border-[#D4AF37]/50 focus:outline-none transition-colors text-sm"
                    />
                    <input
                      type="tel"
                      placeholder="Phone Number"
                      value={contact.phone}
                      onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                      data-testid="assessment-phone"
                      className="w-full bg-[#FEFDFB]/[0.06] border border-[#FEFDFB]/10 rounded-xl px-5 py-4 text-[#FEFDFB] placeholder-[#FEFDFB]/30 focus:border-[#D4AF37]/50 focus:outline-none transition-colors text-sm"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#FEFDFB]/[0.06]">
              <button
                onClick={() => setStep((s) => Math.max(s - 1, 0))}
                disabled={step === 0}
                data-testid="assessment-back"
                className="flex items-center gap-2 text-[#FEFDFB]/50 text-sm hover:text-[#FEFDFB] transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
              >
                <ArrowLeft size={16} /> Back
              </button>

              {step < questions.length ? (
                questions[step].multi && (
                  <button
                    onClick={() => setStep((s) => s + 1)}
                    disabled={!canProceed()}
                    data-testid="assessment-next"
                    className="flex items-center gap-2 bg-[#D4AF37] text-[#0D1F17] px-8 py-3 text-sm font-semibold rounded-full hover:bg-[#E8D48B] transition-colors disabled:opacity-40"
                  >
                    Continue <ArrowRight size={16} />
                  </button>
                )
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={!canProceed() || submitting}
                  data-testid="assessment-submit"
                  className="flex items-center gap-2 bg-[#D4AF37] text-[#0D1F17] px-8 py-3 text-sm font-semibold rounded-full hover:bg-[#E8D48B] transition-colors disabled:opacity-40"
                >
                  {submitting ? "Analyzing..." : "Get My Recommendation"} <Sparkles size={16} />
                </button>
              )}
            </div>
          </div>
        ) : (
          /* RESULT SCREEN */
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex-1 max-w-2xl mx-auto w-full px-6"
          >
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-[#D4AF37]/15 border border-[#D4AF37]/30 text-[#D4AF37] px-4 py-1.5 rounded-full text-[11px] tracking-[0.15em] uppercase font-semibold mb-4">
                <Sparkles size={12} /> Your Personalized Recommendation
              </div>
              <h2 className="font-serif text-4xl md:text-5xl text-[#FEFDFB] tracking-tight" data-testid="result-program">
                {result.program}
              </h2>
              <p className="font-serif text-lg italic text-[#D4AF37] mt-2">{result.tagline}</p>
            </div>

            <div className="bg-[#FEFDFB]/[0.04] border border-[#FEFDFB]/10 rounded-2xl p-8 mb-8" data-testid="result-details">
              <p className="text-[#FEFDFB]/60 text-sm leading-relaxed mb-6">{result.reason}</p>

              <div className="space-y-3 mb-6">
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37] font-bold">What's Included</p>
                {result.features.map((f) => (
                  <div key={f} className="flex items-center gap-3">
                    <Leaf size={14} className="text-[#D4AF37] flex-shrink-0" />
                    <span className="text-[#FEFDFB]/70 text-sm">{f}</span>
                  </div>
                ))}
              </div>

              <div className="pt-6 border-t border-[#FEFDFB]/[0.06]">
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#FEFDFB]/40">Starting from</p>
                <p className="font-serif text-3xl text-[#D4AF37] mt-1">
                  ₹{result.price}
                  {result.priceSuffix && <span className="text-lg text-[#FEFDFB]/30">{result.priceSuffix}</span>}
                  <span className="text-xs text-[#FEFDFB]/30 ml-2">+ 18% GST</span>
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={`/programs/${result.slug}`}
                data-testid="result-view-program"
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0D1F17] px-8 py-3.5 text-sm font-semibold rounded-full hover:bg-[#E8D48B] transition-colors"
              >
                View Program Details <ArrowRight size={16} />
              </a>
              <a
                href="/consultation"
                data-testid="result-book-call"
                className="inline-flex items-center justify-center gap-2 border border-[#FEFDFB]/20 text-[#FEFDFB] px-8 py-3.5 text-sm rounded-full hover:bg-[#FEFDFB]/10 transition-colors"
              >
                Book a Free Consultation
              </a>
            </div>

            <button
              onClick={() => { setStep(0); setAnswers({}); setContact({ name: "", email: "", phone: "" }); setResult(null); }}
              data-testid="result-retake"
              className="block mx-auto mt-6 text-[#FEFDFB]/30 text-sm hover:text-[#FEFDFB]/60 transition-colors"
            >
              Retake Assessment
            </button>
          </motion.div>
        )}
      </div>

      <Footer />
    </div>
  );
}
